import { useMemo, useState } from "react";
import clsx from "clsx";
import { Pill } from "../ui/Pill";
import { FamilyDrawer, type FamilyDrawerData } from "./FamilyDrawer";
import { sampleChwRoster, type SampleFamily, type SampleFamilyStatus } from "../../demo/chwRoster";
import { useInterviewState } from "../../state/InterviewProvider";
import { selectProgress } from "../../engine/selectors";
import { familyWellnessTemplate } from "../../templates/familyWellness";
import type { FieldState, InterviewState } from "../../engine/types";

type Tone = "neutral" | "sage" | "warn" | "danger" | "clay";

const ASKABLE_IDS = familyWellnessTemplate.fields.filter((f) => f.askable).map((f) => f.id);

const SAMPLE_STATUS_LABEL: Record<SampleFamilyStatus, string> = {
  complete: "Complete",
  needs_followup: "Needs follow-up",
  not_started: "Not started",
  safety_flagged: "Safety flagged",
};

const SAMPLE_STATUS_TONE: Record<SampleFamilyStatus, Tone> = {
  complete: "sage",
  needs_followup: "warn",
  not_started: "neutral",
  safety_flagged: "danger",
};

/** §14: rows needing follow-up sort first — sample statuses `needs_followup`
 * and `safety_flagged` count, matching the live row's own follow-up rule below. */
function needsFollowupForSample(status: SampleFamilyStatus): boolean {
  return status === "needs_followup" || status === "safety_flagged";
}

function countFilled(fields: Record<string, FieldState>): number {
  return ASKABLE_IDS.filter((id) => fields[id]?.status === "filled").length;
}

function liveStatus(state: InterviewState): { label: string; tone: Tone; needsFollowup: boolean } {
  // §14/§21 item 12: the live row must flip to the SAME follow-up/safety-flagged
  // visual treatment as a sample row the instant `state.safety.status` is
  // "paused" — it is driven by the real reducer state, never a local guess.
  if (state.safety.status === "paused") {
    return { label: "Safety flagged", tone: "danger", needsFollowup: true };
  }
  if (state.followup.needed) {
    return { label: "Needs follow-up", tone: "warn", needsFollowup: true };
  }
  if (state.phase === "complete") {
    return { label: "Complete", tone: "sage", needsFollowup: false };
  }
  if (state.phase === "idle") {
    return { label: "Not started", tone: "neutral", needsFollowup: false };
  }
  return { label: "In progress", tone: "neutral", needsFollowup: false };
}

type Row = {
  key: string;
  kind: "sample" | "live";
  displayName: string;
  statusLabel: string;
  statusTone: Tone;
  needsFollowup: boolean;
  fieldSummary: string;
  onOpen: () => void;
};

type DrawerSelection = { kind: "sample"; family: SampleFamily } | { kind: "live" } | null;

/**
 * Renders the illustrative sample roster (`demo/chwRoster.ts`) plus ONE
 * additional row bound to the actual live `InterviewState` (§14). The live
 * row is visually marked "This device — live" so it can never be mistaken
 * for one of the sample families, and its drawer reads `state` directly
 * (not a snapshot) so it keeps updating while open.
 */
export function RosterTable() {
  const { state } = useInterviewState();
  const [selection, setSelection] = useState<DrawerSelection>(null);

  const rows: Row[] = useMemo(() => {
    const sampleRows: Row[] = sampleChwRoster.map((family) => ({
      key: family.id,
      kind: "sample",
      displayName: family.displayName,
      statusLabel: SAMPLE_STATUS_LABEL[family.status],
      statusTone: SAMPLE_STATUS_TONE[family.status],
      needsFollowup: needsFollowupForSample(family.status),
      fieldSummary: `${countFilled(family.fields)}/${ASKABLE_IDS.length} fields`,
      onOpen: () => setSelection({ kind: "sample", family }),
    }));

    const progress = selectProgress(state);
    const live = liveStatus(state);
    const liveRow: Row = {
      key: "__live__",
      kind: "live",
      displayName: "Current session",
      statusLabel: live.label,
      statusTone: live.tone,
      needsFollowup: live.needsFollowup,
      fieldSummary: `${progress.filled}/${progress.total} fields`,
      onOpen: () => setSelection({ kind: "live" }),
    };

    // Stable sort: follow-up-needing rows (sample or live) first, original
    // order preserved within each group (Array#sort is stable per spec).
    return [...sampleRows, liveRow].sort((a, b) => Number(b.needsFollowup) - Number(a.needsFollowup));
  }, [state]);

  const drawerData: FamilyDrawerData | null =
    selection === null
      ? null
      : selection.kind === "sample"
        ? {
            displayName: selection.family.displayName,
            isLive: false,
            fields: selection.family.fields,
            safety: selection.family.safety,
            followup: selection.family.followup,
          }
        : {
            displayName: "Current session — this device",
            isLive: true,
            fields: state.fields,
            safety: state.safety,
            followup: state.followup,
          };

  return (
    <div className="space-y-3">
      <table className="w-full border-separate border-spacing-y-2 text-left text-sm">
        <thead>
          <tr className="text-xs uppercase tracking-wide text-ink-soft">
            <th className="px-3 py-1 font-medium">Household</th>
            <th className="px-3 py-1 font-medium">Status</th>
            <th className="px-3 py-1 font-medium">Fields</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.key}
              data-testid="roster-row"
              data-followup={row.needsFollowup}
              data-live={row.kind === "live"}
              className={clsx(
                "cursor-pointer rounded-lg border",
                row.kind === "live" ? "border-clay/50 bg-clay-dim/40" : "border-border bg-paper",
              )}
              onClick={row.onOpen}
              tabIndex={0}
              role="button"
              aria-label={`Open details for ${row.displayName}`}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  row.onOpen();
                }
              }}
            >
              <td className="rounded-l-lg px-3 py-2">
                <div className="flex items-center gap-2">
                  <span className="font-medium text-ink">{row.displayName}</span>
                  {row.kind === "live" && <Pill tone="clay">This device — live</Pill>}
                </div>
              </td>
              <td className="px-3 py-2">
                <Pill tone={row.statusTone}>{row.statusLabel}</Pill>
              </td>
              <td className="rounded-r-lg px-3 py-2 text-ink-soft">{row.fieldSummary}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <FamilyDrawer open={drawerData !== null} onClose={() => setSelection(null)} data={drawerData} />
    </div>
  );
}
